import HiddenInput from "@/components/form/components/HiddenInput";
import LabelWithTooltip from "@/components/form/labels/LabelWithTooltip";
import { InputNumber } from "@/components/InputNumber";
import { resolveFormValidationError, setErrorsToForm } from "@/utils/formUtils";
import { validations } from "@/utils/validationUtils";
import { Checkbox, Col, Form, Input, Modal, Row } from "antd";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useCreateBailAccountMovement, useUpdateBailAccountMovement } from "../../../../../common/api/queries";
import { ModalSizes, rowGutter } from "../../../../../common/constants";
import type { UUID } from "../../../../../typings/global";
import { CommissionsCategory } from "../../../enums";
import { BailAccountMovement, CreateUpdateBailAccountMovement } from "../../types";

interface Props {
  movement?: BailAccountMovement;
  agentId: UUID;
  category: CommissionsCategory;
  open: boolean;
  onFormCancel: () => void;
}

export const BailAccountMovementForm = ({ movement, agentId, category, open, onFormCancel }: Props) => {
  const { t } = useTranslation();

  const [form] = Form.useForm<CreateUpdateBailAccountMovement>();

  const createBailAccountMovement = useCreateBailAccountMovement();
  const updateBailAccountMovement = useUpdateBailAccountMovement();

  useEffect(() => {
    if (open && movement) {
      form.setFieldsValue({
        optimisticLockVersion: movement.optimisticLockVersion,
        amount: movement.amount,
        processedManually: movement.processedManually,
        note: movement.note,
      });
    }
  }, [open, movement, form]);

  const handleFormSubmit = () => {
    form
      .validateFields()
      .then((values) => {
        if (movement) {
          updateBailAccountMovement.mutate(
            { id1: agentId, id2: movement.id, object: values },
            {
              onSuccess: onFormCancel,
              onError: (error) => setErrorsToForm(form, "commissions.bailAccount.attrs.movements", error.violations),
            },
          );
        } else {
          createBailAccountMovement.mutate(
            { id: agentId, object: values },
            {
              onSuccess: onFormCancel,
              onError: (error) => setErrorsToForm(form, "commissions.bailAccount.attrs.movements", error.violations),
            },
          );
        }
      })
      .catch(resolveFormValidationError);
  };

  return (
    <Modal
      width={ModalSizes.MEDIUM}
      open={open}
      title={
        movement
          ? t("commissions.bailAccount.titles.updateMovement")
          : t("commissions.bailAccount.titles.createMovement")
      }
      okText={t("common.save")}
      cancelText={t("common.cancel")}
      confirmLoading={createBailAccountMovement.isPending || updateBailAccountMovement.isPending}
      afterClose={() => form.resetFields()}
      onOk={handleFormSubmit}
      onCancel={onFormCancel}
      mask={{ closable: false }}
    >
      <Form form={form} layout="vertical" name="bailAccountMovementForm">
        <HiddenInput name="optimisticLockVersion" />
        <HiddenInput name="category" initialValue={category} />

        <Row gutter={rowGutter}>
          <Col span={12}>
            <Form.Item
              name="amount"
              label={t("commissions.bailAccount.attrs.movements.amount")}
              rules={[validations.notNull, validations.positiveOrNegativeNumber]}
            >
              <InputNumber type="decimal" addon="euro" />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              name="processedManually"
              rules={[validations.notNull]}
              initialValue={false}
              className="form-item-without-label"
              valuePropName="checked"
            >
              <Checkbox>
                <LabelWithTooltip
                  label={t("commissions.bailAccount.attrs.movements.processedManually")}
                  tooltip={t("commissions.bailAccount.helpers.processedManuallyHint")}
                />
              </Checkbox>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={rowGutter}>
          <Col span={24}>
            <Form.Item
              name="note"
              label={t("commissions.bailAccount.attrs.movements.note")}
              rules={[validations.size(1, 8192)]}
            >
              <Input />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};
